import { createSlice, PayloadAction, Dispatch } from "@reduxjs/toolkit";
import { History } from "history";
import jwtDecode from "jwt-decode";
import { IJwtDecodeBody } from "types/jwtDecode";
import API from "@/api/serverCall";
import { getAccessToken } from "@/utils/auth";
import { resetOrganizationState } from "@/redux/organization/actions";
import { resetUserState } from "@/redux/user/actions";
import { setApiClient } from "@/redux/global/actions";
import { getRestApiClient } from "@/api/api";
import { getStateKeyList } from "@/utils/redux/state";

const name = "auth";

export interface IAuthState {
	isLoading: boolean;
	isAuthenticated: boolean | null;
	user: object;
}

const initialState: IAuthState = {
	isLoading: false,
	isAuthenticated: null,
	user: {},
};

export const authSlice = createSlice({
	name,
	initialState,
	reducers: {
		startLoading: (state) => {
			state.isLoading = true;
		},
		setAuth: (
			state,
			action: PayloadAction<{ isAuthenticated: boolean; user: object }>,
		) => {
			state.isLoading = false;
			state.isAuthenticated = action.payload.isAuthenticated;
			state.user = action.payload.user;
		},
		loginSuccess: (state, action: PayloadAction<{ user: object }>) => {
			state.isAuthenticated = true;
			state.user = action.payload.user;
		},
		resisterSuccess: (state, action: PayloadAction<{ user: object }>) => {
			state.isAuthenticated = true;
			state.user = action.payload.user;
		},
		logoutSuccess: (state) => {
			state.isAuthenticated = false;
			state.user = {};
		},
	},
});

export const AUTH_STATE_KEY_LIST = getStateKeyList(initialState);

export const { startLoading, setAuth, loginSuccess, logoutSuccess, resisterSuccess } =
	authSlice.actions;

const isValidToken: (token: string) => boolean = (accessToken: string) => {
	if (!accessToken) {
		return false;
	}
	const decoded: IJwtDecodeBody = jwtDecode<IJwtDecodeBody>(accessToken);
	const currentTime = Date.now() / 1000;

	return decoded.exp > currentTime;
};

const setSession: (token: string | null) => void = (accessToken: string | null) => {
	if (accessToken === null) {
		localStorage.removeItem("accessToken");
	} else {
		localStorage.setItem("accessToken", accessToken);
	}
};

export const getInitialize = (history: History) => {
	return async (dispatch: Dispatch) => {
		dispatch(startLoading());
		try {
			const accessToken = getAccessToken();

			if (accessToken && isValidToken(accessToken)) {
				dispatch(setApiClient(getRestApiClient(accessToken)));
				setSession(accessToken);
				const totalUser: object = {
					token: accessToken,
				};
				dispatch(
					setAuth({
						isAuthenticated: true,
						user: totalUser,
					}),
				);
			} else {
				dispatch(setApiClient(getRestApiClient()));
				dispatch(
					setAuth({
						isAuthenticated: false,
						user: {},
					}),
				);
				setSession(null);
				history.push("/auth/login");
				dispatch(resetOrganizationState());
			}
		} catch (error) {
			console.error(error);
			dispatch(
				setAuth({
					isAuthenticated: false,
					user: {},
				}),
			);
			dispatch(setApiClient(getRestApiClient()));
			setSession(null);
			history.push("/auth/login");
			dispatch(resetOrganizationState());
		}
	};
};
type profileObjType = {
	email: string;
	familyName: string;
	givenName: string;
	googleId: string;
};
type SignInRequestType = {
	profileObj?: profileObjType;
	email: string;
	password: string;
	snsId: string;
	tokenId?: string;
	loginType: string;
};
export interface ILogin {
	requestData: SignInRequestType;
	callback: () => void;
	enqueueSnackbar: any;
}
type SignUpRequestType = {
	nameFirst: string;
	nameLast: string;
	email: string;
	password: string;
	snsId: string;
	tokenId?: string;
	loginType: string;
	token?: string;
};
export interface ISignup {
	requestData: SignUpRequestType;
	callback: () => void;
	enqueueSnackbar: any;
}

export const login = ({ requestData, callback, enqueueSnackbar }: ILogin) => {
	return async (dispatch: Dispatch) => {
		dispatch(startLoading());
		try {
			API.POST<string>({
				link: `/user/signIn`, // 두잉랩서버 로그인토큰발급 api임
				body: requestData,
				loginRequest: true,
			})
				.then((response) => {
					if (response.data) {
						setSession(response.data);
						const totalUser = {
							token: response.data,
						};
						dispatch(loginSuccess({ user: totalUser }));
						callback();
					}
				})
				.catch((error) => {
					if (error.response.status === 404) {
						// 구글로그인을 하려는데 , 해당구글아이디로, 로컬회원가입이 안되있는 유저처리
						if (requestData.loginType === "google") {
							if (requestData.profileObj) {
								const { email, familyName, givenName, googleId } =
									requestData.profileObj;
								const SocialrequestData = {
									nameFirst: givenName,
									nameLast: familyName,
									email,
									password: "",
									snsId: googleId,
									tokenId: requestData.tokenId,
									loginType: "google",
								};
								socialSignUp({
									requestData: SocialrequestData,
									callback,
									enqueueSnackbar,
								});
							}
						} else {
							// 일반적인 가입되어있지 않은 유저가 로그인하려할때
							enqueueSnackbar("입력하신 정보가 올바르지 않습니다.", {
								variant: "info",
							});
						}
					} else {
						enqueueSnackbar("일시적 오류입니다", {
							variant: "info",
						});
					}
				});
		} catch (error) {
			console.error(error);
		}
	};
};

const socialSignUp = ({ requestData, callback, enqueueSnackbar }: ISignup) => {
	startLoading();
	try {
		API.POST<string>({
			link: `/user/signUp`, // 두잉랩서버 로그인토큰발급 api임
			body: requestData,
			loginRequest: true,
		})
			.then((response) => {
				if (response.data) {
					setSession(response.data);
					const totalUser = {
						token: response.data,
					};
					loginSuccess({ user: totalUser });
					callback();
				}
			})
			.catch((error) => {
				if (error.response.status === 409) {
					enqueueSnackbar("이미 존재하는 계정입니다", {
						variant: "info",
					});
				} else {
					enqueueSnackbar("일시적 오류입니다", {
						variant: "info",
					});
				}
			});
	} catch (error) {
		console.error(error);
	}
};

export const signUp = ({ requestData, callback, enqueueSnackbar }: ISignup) => {
	return async (dispatch: Dispatch) => {
		dispatch(startLoading());
		try {
			API.POST<string>({
				link: "token" in requestData ? `/user/signUp/crew` : `/user/signUp`, // 두잉랩서버 로그인토큰발급 api임
				body: requestData,
				loginRequest: true,
			})
				.then((response) => {
					if (response.data) {
						setSession(response.data);
						const totalUser = {
							token: response.data,
						};
						dispatch(loginSuccess({ user: totalUser }));
						callback();
					}
				})
				.catch((error) => {
					if (error.response.status === 409) {
						enqueueSnackbar("이미 존재하는 계정입니다", {
							variant: "info",
						});
					} else {
						enqueueSnackbar("일시적 오류입니다", {
							variant: "info",
						});
					}
				});
		} catch (error) {
			console.error(error);
		}
	};
};

export const logout = () => {
	return async (dispatch: Dispatch) => {
		setSession(null);
		dispatch(resetUserState());
		dispatch(logoutSuccess());
		dispatch(resetOrganizationState());
	};
};

export default authSlice.reducer;
